import Button, { ButtonProps } from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Link from 'next/link'
import React from 'react'
import { Colors } from '../../constants/theme'

interface StyledBaseButtonProps extends ButtonProps {
  width?: number | string
  height?: number | string
  size?: ButtonProps['size']
  borderradius?: number
  fsize?: number | string
  fweight?: number | string
  ffamily?: string
  blueborder?: number
  whiteborder?: number
  border?: string
  customcolor?: string
  customcontrastcolor?: string
  rounded?: number
  shadow?: number
  underline?: number
  nopadding?: number
}

const StyledBaseButton = ({
  width,
  height,
  size,
  borderradius,
  fsize,
  fweight,
  ffamily,
  blueborder,
  whiteborder,
  border,
  customcolor,
  customcontrastcolor,
  rounded,
  shadow,
  underline,
  nopadding,
  variant,
  className,
  style,
  sx: callerSx,
  loadingIndicator,
  ...rest
}: StyledBaseButtonProps) => {
  const computedWidth =
    typeof width === 'number' ? `${width}px` : (width as string | undefined)
  const computedHeight =
    size === 'large'
      ? '50px'
      : height !== undefined
        ? typeof height === 'number'
          ? `${height}px`
          : height
        : '40px'
  const computedFontSize =
    typeof fsize === 'number' ? `${fsize}px` : (fsize as string | undefined)
  const computedBorderRadius = rounded
    ? '100px'
    : borderradius
      ? `${borderradius}px`
      : '4px'

  // Variant-based color logic
  let variantBg: string | undefined
  let variantColor: string | undefined
  let variantBorder: string | undefined
  let hoverBg: string | undefined
  if (variant === 'contained') {
    variantBg = customcolor
    variantColor = customcontrastcolor || '#fff'
  } else if (variant === 'outlined') {
    variantBorder = `1px solid ${customcolor}`
    variantColor = customcontrastcolor || customcolor
  } else if (variant === 'text') {
    variantColor = customcontrastcolor || customcolor
    if (customcontrastcolor) {
      hoverBg = `${customcontrastcolor}0A`
    }
  } else {
    variantColor = customcontrastcolor || customcolor
  }

  const borderFinal = border
    ? border
    : blueborder
      ? `3.5px solid ${Colors.buttonBorderBlue}`
      : whiteborder
        ? `3.5px solid ${Colors.grey400}`
        : variantBorder

  const paddingTopExtra = blueborder || whiteborder ? '8px' : undefined
  const padding = nopadding ? '0px' : '6px 18px'

  const isLoading = Boolean(rest.loading)
  const indicatorColor = variantColor || customcolor || 'currentColor'

  // Disabled state must visibly win even when a caller hardcodes a `bg-*`
  // className. Tailwind `important: true` makes those `!important`, which would
  // otherwise beat MUI's disabled styles AND our `sx` backgroundColor — so a
  // disabled button kept looking enabled (blue). A `disabled:` utility carries
  // the `:disabled` pseudo-class, giving it higher specificity than a plain
  // `.bg-*`; with both `!important`, specificity decides, so the grey wins.
  // Skip while loading: a loading button is auto-disabled by MUI, and the `sx`
  // hides its text (color: transparent) to show the spinner — applying the grey
  // text override here would render the label AND the spinner together.
  const disabledClass = isLoading
    ? ''
    : variant === 'contained'
      ? 'disabled:bg-grey-300 disabled:text-grey-500 disabled:shadow-none'
      : 'disabled:text-grey-500 disabled:shadow-none'

  // Use `sx` instead of `style` here. MUI's emotion engine compiles `sx` to a
  // generated className whose CSS is injected into a stylesheet — the DOM
  // element does NOT get an inline `style=` attribute. The previous inline
  // `style={mergedStyle}` was the third confirmed React 19 #418 trigger
  // surfaced by at-error captureHydrationDiff (2026-05-27, after the
  // `<Link>` fix shipped in f0b73cb5): SSR emitted the style attribute as
  // `box-shadow:none;text-transform:none;padding:6px 18px;padding-top:8px;...`
  // (compact, with the padding override as a SEPARATE property), while
  // CSR DOM normalization rewrote it as `box-shadow: none; ...
  // padding: 8px 18px 6px; ...` (spaced, with padding shorthand collapsed).
  // Confirmed across Googlebot Smartphone, Chrome 148 Desktop, Chrome Mobile
  // on /pension-estacionamiento/flotillas, /anfitrion/negocio, and listing
  // pages. Routing through `sx` eliminates the inline style attribute
  // entirely — see CLAUDE.md "Inline `style={}` on any SSR'd element".
  const mergedSx = {
    boxShadow: shadow ? '0px 4px 8px -4px rgba(0, 0, 0, 0.25)' : 'none',
    textTransform: 'none',
    textDecoration: underline ? 'underline' : 'none',
    fontSize: computedFontSize,
    fontWeight: fweight ? fweight : 500,
    fontFamily: ffamily,
    lineHeight: '19px',
    whiteSpace: 'nowrap',
    width: computedWidth,
    height: computedHeight,
    backgroundColor: variantBg,
    color: isLoading ? 'transparent' : variantColor,
    opacity: isLoading ? 0.7 : undefined,
    filter: isLoading ? 'saturate(0.85)' : undefined,
    // Caller-passed `style` is folded into sx — callers that pass `style`
    // to a BaseButton in this codebase are passing static CSS that's safe
    // to express as a sx object; spreading after our defaults so caller
    // overrides win, matching the prior behavior.
    ...(style as Record<string, unknown>),
    ...(callerSx as Record<string, unknown> | undefined),
    // Properties that override MUI's theme defaults need `!important`.
    // The previous inline `style={}` always won because inline styles have
    // the highest CSS specificity, but that caused React 19 hydration #418.
    // Moving to `sx` generates a class that competes with MUI's theme-level
    // `.MuiButton-root { border-radius: 4px; padding: ... }` — without
    // `!important`, MUI's theme wins and buttons lose their rounded shape,
    // padding, and border. Tried `&&` doubled-selector first but it still
    // lost to MUI v7's theme specificity in production.
    borderRadius: `${computedBorderRadius} !important`,
    padding: `${padding} !important`,
    paddingTop: paddingTopExtra ? `${paddingTopExtra} !important` : undefined,
    border: borderFinal ? `${borderFinal} !important` : undefined
  }

  return (
    <Button
      variant={variant}
      size={size}
      className={`hover:shadow-none hover:brightness-110 ${disabledClass} ${hoverBg ? '' : ''} ${className ?? ''}`.trim()}
      sx={mergedSx}
      loadingIndicator={
        loadingIndicator ?? (
          <CircularProgress size={18} sx={{ color: indicatorColor }} />
        )
      }
      {...rest}
    />
  )
}

export interface BaseButtonProps extends ButtonProps {
  width?: number | string
  height?: number | string
  href?: string
  borderRadius?: number
  fontSize?: number
  fontWeight?: number | string
  fontFamily?: string
  bold?: boolean
  customColor?: string
  customContrastColor?: string
  rounded?: boolean
  shadow?: boolean
  underline?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: string | any
  blueBorder?: boolean
  whiteBorder?: boolean
  border?: string
  newTab?: boolean
  prefetch?: boolean
  noPadding?: boolean
}

const BaseButton = (props: BaseButtonProps) => {
  const {
    rounded,
    width,
    height,
    href,
    size,
    borderRadius,
    bold,
    border,
    blueBorder,
    whiteBorder,
    customColor,
    fontSize = 16,
    fontWeight,
    fontFamily,
    customContrastColor,
    shadow,
    underline,
    sx,
    newTab,
    prefetch,
    noPadding,
    ...rest
  } = props
  const color =
    customColor === undefined && rest.variant === 'contained'
      ? rest.disabled
        ? Colors.grey400
        : Colors.primary
      : customColor

  if (href && newTab) {
    return (
      <StyledBaseButton
        width={width}
        height={height}
        size={size}
        borderradius={borderRadius}
        fsize={fontSize}
        fweight={bold ? 'bold' : fontWeight}
        ffamily={fontFamily}
        customcolor={customColor}
        blueborder={blueBorder ? 1 : 0}
        whiteborder={whiteBorder ? 1 : 0}
        border={border}
        customcontrastcolor={customContrastColor}
        rounded={rounded ? 1 : 0}
        shadow={shadow ? 1 : 0}
        underline={underline ? 1 : 0}
        nopadding={noPadding ? 1 : 0}
        sx={sx}
        // New Tab
        href={href}
        rel="noreferrer"
        {...{ target: '_blank' }}
        {...rest}
      />
    )
  }

  const button = (
    <StyledBaseButton
      width={width}
      height={height}
      size={size}
      borderradius={borderRadius}
      fsize={fontSize}
      fweight={bold ? 'bold' : fontWeight}
      ffamily={fontFamily}
      customcolor={color}
      blueborder={blueBorder ? 1 : 0}
      whiteborder={whiteBorder ? 1 : 0}
      border={border}
      customcontrastcolor={customContrastColor}
      rounded={rounded ? 1 : 0}
      shadow={shadow ? 1 : 0}
      underline={underline ? 1 : 0}
      nopadding={noPadding ? 1 : 0}
      sx={sx}
      {...rest}
    />
  )

  return href ? (
    // Inline `style={{ textDecoration: 'none' }}` (plus the conditional
    // width key) on this <Link> was the upstream trigger for React 19 #418
    // hydration mismatches firing on EVERY page that renders an href-bearing
    // BaseButton — including LandingNav's "Subir tu espacio" CTA, the Login
    // link, anfitrion CTAs, etc. Next.js <Link> renders as <a>; SSR serializes
    // the inline style as `style="text-decoration:none"` while React's post-
    // hydration DOM re-serializes via property API as `style="text-decoration: none;"`
    // (whitespace + trailing semi added). React 19's byte-strict attribute
    // comparison fires #418. Attribute order also drifts (`<a style ... href>`
    // SSR vs `<a href ... style>` CSR). Confirmed via at-error snapshot on
    // /anfitrion/tradicional 2026-05-26 — see CLAUDE.md "hydration_diff log
    // capture timing" and "Inline `style={}` on any SSR'd element is a
    // hydration risk". Use className only — no inline style.
    <Link
      href={href}
      passHref
      prefetch={prefetch}
      className={
        rest.fullWidth ? 'inline-block w-full no-underline' : 'no-underline'
      }
    >
      {button}
    </Link>
  ) : (
    button
  )
}

export default React.memo(BaseButton)
